import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { groupsAPI, groupsExtendedAPI, reportsAPI } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Users, MessageSquare, Calendar, Plus, Heart, 
  MessageCircle, Send, Video, Clock, Check, X, User, Pencil, Rocket,
  Mail, Flag, AlertTriangle, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const REPORT_CATEGORIES = [
  { value: "harassment", label: "Harassment or bullying" },
  { value: "spam", label: "Spam or self-promotion" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "off_topic", label: "Off-topic or disrupting" },
  { value: "other", label: "Other" }
];

const SUPPORT_EMAIL = "mo.stemen@arcanacoaching.com";

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("discussion");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', theme: '' });
  
  // Dialog states
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [meetupDialogOpen, setMeetupDialogOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  
  // Report dialog states
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({
    report_type: '',
    target_id: '',
    target_name: '',
    category: '',
    description: ''
  });
  
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [newMeetup, setNewMeetup] = useState({ 
    title: '', 
    description: '', 
    scheduled_at: '', 
    video_link: '' 
  });

  const isFacilitator = group?.facilitator_id === user?.id;
  const isDraft = group && !group.is_launched;

  const loadGroupData = useCallback(async () => {
    try {
      const [groupRes, postsRes, meetupsRes] = await Promise.all([
        groupsAPI.getById(groupId),
        groupsAPI.getPosts(groupId),
        groupsAPI.getMeetups(groupId)
      ]);
      setGroup(groupRes.data);
      setPosts(postsRes.data);
      setMeetups(meetupsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You must be a member to view this group');
        navigate('/groups');
      } else {
        console.error('Failed to load group data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  useEffect(() => {
    loadGroupData();
  }, [loadGroupData]);

  const openEditDialog = () => {
    setEditData({
      name: group.name,
      description: group.description,
      theme: group.theme
    });
    setEditDialogOpen(true);
  };

  const handleEditGroup = async (e) => {
    e.preventDefault();
    try {
      await groupsExtendedAPI.update(groupId, editData);
      toast.success('Group updated!');
      setEditDialogOpen(false);
      await loadGroupData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    }
  };

  const handleLaunchGroup = async () => {
    try {
      await groupsExtendedAPI.launch(groupId);
      toast.success('Your group is now live! Members can start joining.');
      await loadGroupData();
    } catch (error) {
      toast.error('Failed to launch group');
    }
  };

  const openReportDialog = (type, targetId, targetName) => {
    setReportData({
      report_type: type,
      target_id: targetId,
      target_name: targetName,
      category: '',
      description: ''
    });
    setReportDialogOpen(true);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportData.category || !reportData.description.trim()) {
      toast.error('Please select a category and provide details');
      return;
    }
    try {
      await reportsAPI.submit({
        ...reportData,
        group_id: groupId,
        group_name: group.name
      });
      toast.success('Report submitted. An admin will review it shortly.');
      setReportDialogOpen(false);
      setReportData({ report_type: '', target_id: '', target_name: '', category: '', description: '' });
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const getSupportMailtoLink = () => {
    const subject = encodeURIComponent(`Small Groups Support - ${group?.name || 'General'}`);
    const body = encodeURIComponent(`Hi Mo,\n\nI need help with the group "${group?.name}".\n\n[Please describe your question or issue here]\n\nThank you!`);
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      await groupsAPI.createPost(groupId, newPost);
      toast.success('Post created!');
      setPostDialogOpen(false);
      setNewPost({ title: '', content: '' });
      const postsRes = await groupsAPI.getPosts(groupId);
      setPosts(postsRes.data);
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim()) return;
    try {
      await groupsAPI.addReply(groupId, postId, replyContent);
      toast.success('Reply added!');
      setReplyingTo(null);
      setReplyContent('');
      const postsRes = await groupsAPI.getPosts(groupId);
      setPosts(postsRes.data);
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  const handleReact = async (postId, reactionType) => {
    try {
      await groupsAPI.reactToPost(groupId, postId, reactionType);
      const postsRes = await groupsAPI.getPosts(groupId);
      setPosts(postsRes.data);
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const handleCreateMeetup = async (e) => {
    e.preventDefault();
    try {
      await groupsAPI.createMeetup(groupId, newMeetup);
      toast.success('Meetup scheduled!');
      setMeetupDialogOpen(false);
      setNewMeetup({ title: '', description: '', scheduled_at: '', video_link: '' });
      const meetupsRes = await groupsAPI.getMeetups(groupId);
      setMeetups(meetupsRes.data);
    } catch (error) {
      toast.error('Failed to create meetup');
    }
  };

  const handleRSVP = async (meetupId, status) => {
    try {
      await groupsAPI.rsvp(groupId, meetupId, status);
      toast.success(status === 'attending' ? 'See you there!' : 'Maybe next time');
      const meetupsRes = await groupsAPI.getMeetups(groupId);
      setMeetups(meetupsRes.data);
    } catch (error) {
      toast.error('Failed to RSVP');
    }
  };

  const getReactionCount = (post, type) => {
    return post.reactions?.filter(r => r.reaction_type === type).length || 0;
  };

  const hasUserReacted = (post, type) => {
    return post.reactions?.some(r => r.user_id === user?.id && r.reaction_type === type);
  };

  const getUserRSVP = (meetup) => {
    return meetup.rsvps?.find(r => r.user_id === user?.id);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading group...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!group) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Group not found</p>
          <Button onClick={() => navigate('/groups')} variant="link">
            Back to Groups
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="group-detail-page">
        {/* Header */}
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/groups')} 
            className="mb-4 -ml-2"
          >
            <ArrowLeft size={16} className="mr-2" />
            All Groups
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {group.theme}
                </span>
                {isDraft && (
                  <span className="text-sm bg-amber-200 text-amber-800 px-3 py-1 rounded-full">
                    Draft
                  </span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-serif mt-3 mb-2">{group.name}</h1>
              <p className="text-muted-foreground">{group.description}</p>
              <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                <Users size={14} />
                <span>{group.members?.length || 0} members</span>
              </div>
            </div>
            
            {/* Facilitator Actions */}
            {isFacilitator && (
              <div className="flex items-center gap-2">
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full" onClick={openEditDialog}>
                      <Pencil size={14} className="mr-2" />
                      Edit
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-2xl max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl">Edit Group</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEditGroup} className="space-y-4">
                      <div>
                        <Label htmlFor="edit-name">Group Name</Label>
                        <Input
                          id="edit-name"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          required
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-description">Description</Label>
                        <Textarea
                          id="edit-description"
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          required
                          className="mt-2 rounded-xl"
                          rows={4}
                        />
                      </div>
                      <Button type="submit" className="w-full rounded-full">
                        Save Changes
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                
                {isDraft && (
                  <Button onClick={handleLaunchGroup} className="rounded-full bg-amber-600 hover:bg-amber-700">
                    <Rocket size={14} className="mr-2" />
                    Launch Group
                  </Button>
                )}
              </div>
            )}
            
            {/* Support & Report Actions (for all members) */}
            {!isFacilitator && (
              <div className="flex items-center gap-2">
                <a href={getSupportMailtoLink()}>
                  <Button variant="outline" className="rounded-full">
                    <Mail size={14} className="mr-2" />
                    Support
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openReportDialog('group', groupId, group.name)}
                  className="rounded-full text-muted-foreground hover:text-red-600"
                >
                  <Flag size={14} className="mr-1" />
                  Report
                </Button>
              </div>
            )}
            
            {/* Facilitator also gets Support button */}
            {isFacilitator && (
              <a href={getSupportMailtoLink()} className="mt-2">
                <Button variant="outline" size="sm" className="rounded-full">
                  <Mail size={14} className="mr-2" />
                  Support
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={20} />
                Report {reportData.report_type === 'post' ? 'Post' : reportData.report_type === 'member' ? 'Member' : 'Group'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitReport} className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Reporting</p>
                <p className="font-medium">{reportData.target_name}</p>
              </div>
              <div>
                <Label>Category *</Label>
                <Select
                  value={reportData.category}
                  onValueChange={(value) => setReportData({ ...reportData, category: value })}
                >
                  <SelectTrigger className="mt-2 rounded-xl">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="report-description">Details *</Label>
                <Textarea
                  id="report-description"
                  value={reportData.description}
                  onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                  placeholder="Please describe what happened..."
                  required
                  className="mt-2 rounded-xl"
                  rows={4}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Reports are reviewed by admins only. Your identity will remain confidential.
              </p>
              <div className="flex gap-2">
                <Button type="submit" className="rounded-full bg-red-600 hover:bg-red-700">
                  <Flag size={14} className="mr-2" />
                  Submit Report
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReportDialogOpen(false)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Draft Notice */}
        {isDraft && (
          <Card className="p-4 rounded-xl border-amber-300 bg-amber-50">
            <p className="text-sm text-amber-800">
              <strong>This group is in draft mode.</strong> Only you can see it. Edit the details and launch when ready for members to join.
            </p>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-3 rounded-full p-1 bg-muted">
            <TabsTrigger value="discussion" className="rounded-full data-[state=active]:bg-background">
              <MessageSquare size={14} className="mr-2" />
              Discussion
            </TabsTrigger>
            <TabsTrigger value="meetups" className="rounded-full data-[state=active]:bg-background">
              <Calendar size={14} className="mr-2" />
              Meetups
            </TabsTrigger>
            <TabsTrigger value="members" className="rounded-full data-[state=active]:bg-background">
              <Users size={14} className="mr-2" />
              Members
            </TabsTrigger>
          </TabsList>

          {/* Discussion Tab */}
          <TabsContent value="discussion" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full">
                    <Plus size={16} className="mr-2" />
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Start a Discussion</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePost} className="space-y-4">
                    <div>
                      <Label htmlFor="post-title">Title</Label>
                      <Input
                        id="post-title"
                        value={newPost.title}
                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                        placeholder="What would you like to discuss?"
                        required
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="post-content">Your Thoughts</Label>
                      <Textarea
                        id="post-content"
                        value={newPost.content}
                        onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                        placeholder="Share your thoughts, questions, or experiences..."
                        required
                        className="mt-2 rounded-xl"
                        rows={5}
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-full">
                      Post
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {posts.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl">
                <MessageSquare className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">No Posts Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start the conversation! Share something with the group.
                </p>
                <Button onClick={() => setPostDialogOpen(true)} className="rounded-full">
                  Create First Post
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.id} className="p-6 rounded-2xl" data-testid={`post-${post.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-serif text-lg">{post.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {post.author_name} · {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {/* Report Post Button */}
                      {post.author_id !== user?.id && (
                        <button
                          onClick={() => openReportDialog('post', post.id, post.title)}
                          className="text-muted-foreground hover:text-red-600 transition-colors p-1 rounded"
                          title="Report this post"
                        >
                          <Flag size={14} />
                        </button>
                      )}
                    </div>
                    
                    <p className="text-muted-foreground whitespace-pre-wrap mb-4">
                      {post.content}
                    </p>
                    
                    {/* Reactions */}
                    <div className="flex items-center gap-2 mb-4">
                      <button
                        onClick={() => handleReact(post.id, 'heart')}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                          hasUserReacted(post, 'heart') 
                            ? 'bg-red-100 text-red-600' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        <Heart size={14} fill={hasUserReacted(post, 'heart') ? 'currentColor' : 'none'} />
                        {getReactionCount(post, 'heart') || ''}
                      </button>
                      <button
                        onClick={() => handleReact(post.id, 'relate')}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                          hasUserReacted(post, 'relate') 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        I relate {getReactionCount(post, 'relate') ? `(${getReactionCount(post, 'relate')})` : ''}
                      </button>
                      <button
                        onClick={() => handleReact(post.id, 'support')}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                          hasUserReacted(post, 'support') 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        Sending support {getReactionCount(post, 'support') ? `(${getReactionCount(post, 'support')})` : ''}
                      </button>
                    </div>
                    
                    {/* Replies */}
                    {post.replies?.length > 0 && (
                      <div className="border-t border-border pt-4 space-y-3">
                        {post.replies.map((reply, idx) => (
                          <div key={reply.id || idx} className="bg-muted/30 p-3 rounded-xl">
                            <p className="text-sm font-medium">{reply.author_name}</p>
                            <p className="text-sm text-muted-foreground mt-1">{reply.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply input */}
                    {replyingTo === post.id ? (
                      <div className="mt-4 flex gap-2">
                        <Input
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write a reply..."
                          className="rounded-xl"
                          onKeyDown={(e) => e.key === 'Enter' && handleReply(post.id)}
                        />
                        <Button size="icon" onClick={() => handleReply(post.id)} className="rounded-full">
                          <Send size={16} />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => { setReplyingTo(null); setReplyContent(''); }}
                          className="rounded-full"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyingTo(post.id)}
                        className="mt-2"
                      >
                        <MessageCircle size={14} className="mr-2" />
                        Reply
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Meetups Tab */}
          <TabsContent value="meetups" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={meetupDialogOpen} onOpenChange={setMeetupDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full">
                    <Plus size={16} className="mr-2" />
                    Schedule Meetup
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Schedule a Meetup</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMeetup} className="space-y-4">
                    <div>
                      <Label htmlFor="meetup-title">Title</Label>
                      <Input
                        id="meetup-title"
                        value={newMeetup.title}
                        onChange={(e) => setNewMeetup({ ...newMeetup, title: e.target.value })}
                        placeholder="e.g., Weekly Check-in"
                        required
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="meetup-description">Description</Label>
                      <Textarea
                        id="meetup-description"
                        value={newMeetup.description}
                        onChange={(e) => setNewMeetup({ ...newMeetup, description: e.target.value })}
                        placeholder="What will we discuss?"
                        required
                        className="mt-2 rounded-xl"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="meetup-datetime">Date & Time</Label>
                      <Input
                        id="meetup-datetime"
                        type="datetime-local"
                        value={newMeetup.scheduled_at}
                        onChange={(e) => setNewMeetup({ ...newMeetup, scheduled_at: e.target.value })}
                        required
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="meetup-link">Video Link (Optional)</Label>
                      <Input
                        id="meetup-link"
                        value={newMeetup.video_link}
                        onChange={(e) => setNewMeetup({ ...newMeetup, video_link: e.target.value })}
                        placeholder="Zoom or Google Meet link"
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-full">
                      Schedule Meetup
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {meetups.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl">
                <Calendar className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">No Meetups Scheduled</h3>
                <p className="text-muted-foreground mb-4">
                  Schedule a video call for the group to connect live.
                </p>
                <Button onClick={() => setMeetupDialogOpen(true)} className="rounded-full">
                  Schedule First Meetup
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {meetups.map((meetup) => {
                  const userRSVP = getUserRSVP(meetup);
                  const attending = meetup.rsvps?.filter(r => r.status === 'attending') || [];
                  const scheduledDate = new Date(meetup.scheduled_at);
                  const isPast = scheduledDate < new Date();
                  
                  return (
                    <Card key={meetup.id} className={`p-6 rounded-2xl ${isPast ? 'opacity-60' : ''}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-serif text-lg">{meetup.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            Scheduled by {meetup.created_by_name}
                          </p>
                        </div>
                        {meetup.video_link && (
                          <a 
                            href={meetup.video_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <Video size={14} />
                            Join Call
                          </a>
                        )}
                      </div>
                      
                      <p className="text-muted-foreground mb-4">{meetup.description}</p>
                      
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock size={14} />
                          {scheduledDate.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users size={14} />
                          {attending.length} attending
                        </span>
                      </div>
                      
                      {/* Attendees */}
                      {attending.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-muted-foreground mb-2">Attending:</p>
                          <div className="flex flex-wrap gap-2">
                            {attending.map((rsvp) => (
                              <span key={rsvp.user_id || rsvp.user_name} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {rsvp.user_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* RSVP buttons */}
                      {!isPast && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={userRSVP?.status === 'attending' ? 'default' : 'outline'}
                            onClick={() => handleRSVP(meetup.id, 'attending')}
                            className="rounded-full"
                          >
                            <Check size={14} className="mr-1" />
                            Attending
                          </Button>
                          <Button
                            size="sm"
                            variant={userRSVP?.status === 'not_attending' ? 'default' : 'outline'}
                            onClick={() => handleRSVP(meetup.id, 'not_attending')}
                            className="rounded-full"
                          >
                            <X size={14} className="mr-1" />
                            Can&apos;t Make It
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-6">
            <Card className="p-6 rounded-2xl">
              <h3 className="font-serif text-lg mb-4">Group Members ({group.members?.length || 0})</h3>
              <div className="space-y-3">
                {group.members?.map((member, idx) => (
                  <div 
                    key={member.user_id || idx} 
                    className="flex items-center justify-between p-3 rounded-xl bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Joined {new Date(member.joined_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.role === 'facilitator' && (
                        <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded-full">
                          Facilitator
                        </span>
                      )}
                      {member.user_id !== user?.id && member.role !== 'facilitator' && (
                        <button
                          onClick={() => openReportDialog('member', member.user_id, member.name)}
                          className="text-muted-foreground hover:text-red-600 transition-colors p-1 rounded"
                          title="Report this member"
                        >
                          <Flag size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
